/**
 * Audio file configuration
 * 
 * This file provides centralized configuration for all audio files used in the application.
 * Audio files can be served from local public folder or from a CDN.
 */

// CDN Configuration
export const CDN_BASE_URL = 'https://pub-107cf891cdd84fd5a31790dcdda19710.r2.dev';

// Empty string uses local files by default

// Sound categories (for UI organization)
export enum SoundCategories {
  NATURE = 'nature',
  AMBIENT = 'ambient',
  INSTRUMENTS = 'instruments',
  MEDITATION = 'meditation',
  URBAN = 'urban',
  SOUNDSCAPES = 'soundscapes'
}

// Sound Configuration Type
export interface SoundConfig {
  id: string;
  name: string;
  path: string;
  category: SoundCategories;
  defaultVolume?: number;
  icon?: React.ElementType;
}

// Sound Presets
export const ambientSoundPresets: SoundConfig[] = [
  {
    id: 'rain',
    name: 'Rain',
    path: `${CDN_BASE_URL}/light-rain-109591.mp3`,
    category: SoundCategories.NATURE,
    defaultVolume: 50
  },
  {
    id: 'ocean',
    name: 'Ocean Waves',
    path: `${CDN_BASE_URL}/ocean-waves-250310.mp3`,
    category: SoundCategories.NATURE,
    defaultVolume: 40
  },
  {
    id: 'forest',
    name: 'Forest',
    path: `${CDN_BASE_URL}/forest-163012.mp3`,
    category: SoundCategories.NATURE,
    defaultVolume: 30
  },
  {
    id: 'wind',
    name: 'Wind',
    path: `${CDN_BASE_URL}/wind-on-beach-77460.mp3`,
    category: SoundCategories.NATURE,
    defaultVolume: 45
  },
  {
    id: 'birds',
    name: 'Birds',
    path: `${CDN_BASE_URL}/birds-19624.mp3`,
    category: SoundCategories.NATURE,
    defaultVolume: 35
  },
  {
    id: 'white-noise',
    name: 'White Noise',
    path: `${CDN_BASE_URL}/WhiteNoise_64kb.mp3`,
    category: SoundCategories.AMBIENT,
    defaultVolume: 25
  },
  {
    id: 'pink-noise',
    name: 'Pink Noise',
    path: `${CDN_BASE_URL}/PinkNoise_64kb.mp3`,
    category: SoundCategories.AMBIENT,
    defaultVolume: 25
  },
  {
    id: 'brown-noise',
    name: 'Brown Noise',
    path: `${CDN_BASE_URL}/BrownianNoise_64kb.mp3`,
    category: SoundCategories.AMBIENT,
    defaultVolume: 25
  },
  {
    id: 'meditation',
    name: 'Meditation',
    path: `${CDN_BASE_URL}/meditation-bowls-23651.mp3`,
    category: SoundCategories.MEDITATION,
    defaultVolume: 30
  },
  {
    id: 'thunder',
    name: 'Thunder',
    path: `${CDN_BASE_URL}/epic-thunder-and-rumbles-no-loops-86150.mp3`,
    category: SoundCategories.NATURE,
    defaultVolume: 35
  },
  {
    id: 'atmospheric',
    name: 'Atmospheric',
    path: `${CDN_BASE_URL}/atmosphere-soundscape-302345.mp3`,
    category: SoundCategories.SOUNDSCAPES,
    defaultVolume: 40
  },
  {
    id: 'space',
    name: 'Space Atmosphere',
    path: `${CDN_BASE_URL}/ambient-soundscapes-007-space-atmosphere-304974.mp3`,
    category: SoundCategories.SOUNDSCAPES,
    defaultVolume: 35
  },
  {
    id: 'tribute',
    name: 'Tribute Wave',
    path: `${CDN_BASE_URL}/ambient-wave-48-tribute-17243.mp3`,
    category: SoundCategories.SOUNDSCAPES,
    defaultVolume: 35
  }
];

// Export a categorized version of the ambient sounds for UI organization
export const categorizedSounds = ambientSoundPresets.reduce<Record<string, SoundConfig[]>>(
  (acc, sound) => {
    const category = sound.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(sound);
    return acc;
  }, 
  {}
);

// Audio file upload instructions
export const AUDIO_UPLOAD_DIRECTORY = 'public/audio';
export const SUPPORTED_AUDIO_FORMATS = ['mp3', 'wav', 'ogg'];

/**
 * Helper to get the absolute URL for a sound
 * @param sound The sound path
 * @returns The absolute URL for the sound
 */
export function getSoundUrl(sound: string): string {
  return `${CDN_BASE_URL}/${sound}`;
}

/**
 * Ensures that a volume value is valid for HTML audio elements (0-1 range)
 * @param volume The volume value to normalize
 * @param fromPercentage Whether the input value is a percentage (0-100)
 * @returns A normalized volume value in the range 0-1
 */
export function normalizeVolume(volume: number, fromPercentage: boolean = true): number {
  // If the volume is already in 0-1 range, just clamp it
  if (!fromPercentage) {
    return Math.max(0, Math.min(1, volume));
  }
  
  // Otherwise, convert from percentage (0-100) to decimal (0-1)
  // Ensure volume is in valid range
  const clampedVolume = Math.max(0, Math.min(100, volume));
  // Convert from percentage to decimal
  return clampedVolume / 100;
}